/**
 * URL and localStorage state management helper
 * Centralizes logic for managing tab and subtab state across views
 */

const TAB_KEY = 'admin.selected_tab';
const VALID_TABS = ['pages', 'servers', 'audit', 'files', 'urlon'];

/**
 * Get the active main tab: URL ?tab= > localStorage > defaultTab
 * @param {string} defaultTab - Fallback if nothing found
 * @returns {string}
 */
export function getTab(defaultTab = 'pages') {
  const urlParams = new URLSearchParams(window.location.search);
  const urlTab = urlParams.get('tab');
  if (urlTab && VALID_TABS.includes(urlTab)) return urlTab;
  const localTab = localStorage.getItem(TAB_KEY);
  if (localTab && VALID_TABS.includes(localTab)) return localTab;
  return defaultTab;
}

/**
 * Persist the active main tab to localStorage
 * @param {string} tab
 */
export function saveTab(tab) {
  localStorage.setItem(TAB_KEY, tab);
}

/**
 * Get the current subtab value, checking URL first, then localStorage
 * @param {string} localStorageKey - Key for localStorage
 * @param {Array<string>} validValues - Array of valid subtab values
 * @param {string} defaultValue - Default value if nothing found
 * @returns {string} The selected subtab value
 */
export function getSubtab(localStorageKey, validValues, defaultValue) {
  // Check URL parameter first
  const urlParams = new URLSearchParams(window.location.search);
  const urlSubtab = urlParams.get('subtab');
  
  if (urlSubtab && validValues.includes(urlSubtab)) {
    return urlSubtab;
  }
  
  // Fall back to localStorage
  const localSubtab = localStorage.getItem(localStorageKey);
  if (localSubtab && validValues.includes(localSubtab)) {
    return localSubtab;
  }
  
  // Return default
  return defaultValue || validValues[0];
}

/**
 * Set the subtab value in both URL and localStorage
 * @param {string} subtab - The subtab value to set
 * @param {string} localStorageKey - Key for localStorage
 */
export function setSubtab(subtab, localStorageKey) {
  // Update localStorage
  localStorage.setItem(localStorageKey, subtab);
  
  // Update URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set('subtab', subtab);
  window.history.replaceState(null, '', '?' + urlParams);
}

/**
 * Backfill the URL with subtab if not present
 * @param {string} subtab - The current subtab value
 */
export function backfillSubtab(subtab) {
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.get('subtab')) {
    urlParams.set('subtab', subtab);
    window.history.replaceState(null, '', '?' + urlParams);
  }
}
